import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useCreateNewLayout } from '../../../hooks/useCreateNewLayout'
import { setActiveLayout } from '../../../reducer/home.reducer'
import ChartWrapper from '../../chartV2/chartWrapper/chartWrapper'
import { setActiveChart } from '../../../reducer/chart.reducer'
import { selectBasketId, selectTradeOrders, selectViewTabs } from '../../../selectors/strategyBuilder'
import EmptyView from '../EmptyView'
import { getStrategyChartParamsFromTradeOrders } from '../../../util/common'
import { ChartViewKey } from '../../../assets/constants'
import { selectPositions } from '../../../selectors/positions'
import { TransactionType } from '../../../proto/generated/order'

const StrategyChartTab = () => {
    const tradeOrders = useSelector(selectTradeOrders)
    const positions = useSelector(selectPositions)
    const basketId = useSelector(selectBasketId);
    const dispatch = useDispatch()
    const createNewLayout = useCreateNewLayout()
    const viewTabs = useSelector(selectViewTabs)
    const [securitiesObj, setSecuritiesObj] = useState<{
        expression: string
        key: string
        securities: string[]
    }>()

    const basketPositions = useMemo(() => positions?.filter(p => p.basketId === basketId), [positions, basketId])

    const selectedTradeOrders = useMemo(() => tradeOrders?.filter(order => order.selected), [tradeOrders])

    useEffect(() => {
        ;(async () => {
            if (!selectedTradeOrders?.length && !basketPositions?.length) {
                setSecuritiesObj(undefined)
                return
            }
            const { param, securities: securityKeys } = getStrategyChartParamsFromTradeOrders(selectedTradeOrders, basketPositions)

            const { newLayoutId } = await createNewLayout(param, true, securityKeys)
            dispatch(setActiveLayout(newLayoutId))

            setSecuritiesObj({
                expression: param,
                securities: Array.from(new Set(securityKeys)),
                key: `Strategy chart - ${JSON.stringify(selectedTradeOrders).slice(0, 20)}`
            })
        })()
    }, [selectedTradeOrders, selectedTradeOrders?.length, basketPositions, basketPositions?.length])

    useEffect(() => {
        if (selectedTradeOrders.length && securitiesObj?.securities?.length) {
            dispatch(
                setActiveChart({
                    chartId: ChartViewKey.STRATEGY_CHART
                })
            )
        }
    }, [selectedTradeOrders.length, securitiesObj])

    const handleOnSecuritySelected = useCallback((key: string, expressionMode: boolean, securities: string[]) => {
        setSecuritiesObj({ key, securities, expression: key })
    }, [])

    const chart = useMemo(() => {
        return (selectedTradeOrders?.length || positions?.length) && securitiesObj?.securities?.length ? (
            <ChartWrapper
                key={securitiesObj.key}
                expression={securitiesObj.expression}
                viewKey={ChartViewKey.STRATEGY_CHART}
                securityList={securitiesObj.securities}
                mode={'EXPRESSION'}
                comparableSecurities={[]}
                primary={false}
                onSecuritySelected={handleOnSecuritySelected}
            />
        ) : null
    }, [handleOnSecuritySelected, selectedTradeOrders.length, positions?.length, securitiesObj])

    const isSquareOffCondition = useMemo(() => {
        if (basketId === "") return false
        const basketPositions = positions?.filter(p => p.basketId === basketId) || []
        const selectedOrders = selectedTradeOrders || []
        if (basketPositions.length === 0 || selectedOrders.length === 0) return false
        if (basketPositions.length === selectedOrders.length) {
            const allMatch = selectedOrders.every((order) => {
                return basketPositions.some((p) => 
                    p.security.code === order.security?.code && 
                    p.security.exchange === order.security?.exchange &&
                    Math.abs(p.closePosition) === Math.abs(order.quantity) && 
                    (p.closePosition > 0) === (order.transactionType === TransactionType.SELL)
                )
            })
            return allMatch
        }
        return false
    }, [selectedTradeOrders, positions, basketId])

    const emptyView = useMemo(() => {
        return viewTabs && !((selectedTradeOrders?.length || positions?.length) && securitiesObj?.securities?.length) ? <EmptyView isSquareOff={isSquareOffCondition} /> : null
    }, [selectedTradeOrders?.length, positions?.length, securitiesObj, viewTabs, isSquareOffCondition])

    const jsxTree = useMemo(() => {
        return (
            <Box sx={{ width: '100%', height: '100%' }}>
                {chart}
                {emptyView}
            </Box>
        )
    }, [chart, emptyView])

    return jsxTree
}

export default memo(StrategyChartTab)
